'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import posthog from 'posthog-js'

import { useDashboardTabs, useResearchFeedAccess } from '@client/components/feed/use-research-feed-access'

/** The dashboard's two sections. `section` claims every page that belongs to
 *  it, so the tabs stay put (and the right one stays highlighted) when you open
 *  a review or a folder. */
const TABS = [
  {
    href: '/projects',
    label: 'Library',
    section: ['/projects', '/write', '/reviews', '/review/', '/folders/', '/upload'],
  },
  { href: '/feed', label: 'Feed', section: ['/feed'] },
] as const

function inSection(pathname: string, prefixes: readonly string[]): boolean {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`))
}

/** One personal library holds projects and peer reviews; its filters live on
 * the dashboard. Organizations and Feed retain their existing access rules. */
export function AppTabs() {
  const pathname = usePathname() ?? ''
  const enabled = useDashboardTabs()
  const feedEnabled = useResearchFeedAccess(enabled)
  const current = TABS.find((t) => inSection(pathname, t.section))

  if (!enabled || !current) return null

  return (
    <div role="navigation" aria-label="Dashboard" className="flex items-center gap-1">
      {TABS.filter((tab) => tab.href !== '/feed' || feedEnabled).map((tab) => {
        const active = tab === current
        // On the section's own page the tab is "here"; deeper in (a review), it's a way back.
        const here = pathname === tab.href
        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={here ? 'page' : active ? 'location' : undefined}
            data-testid={`app-tab-${tab.label.toLowerCase()}`}
            onClick={() => {
              if (tab.href === '/feed' && !active) posthog.capture('research_feed_tab_clicked', { from: pathname })
            }}
            className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {tab.label}
          </Link>
        )
      })}
    </div>
  )
}
